<template>
    <v-container class="px-0">
        <v-row justify="center">
            <v-col cols="12" sm="8" md="6">
                <v-select
                    :label="$t('selectonetofilter')"
                    :items="quizes"
                    item-text="quizTitle"
                    item-value="quizTopicId"
                    v-on:change="filterResult"
                    prepend-icon="emoji_objects"
                    menu-props="auto"
                    return-object
                    clearable
                >
                </v-select>
            </v-col>
        </v-row>
        <v-row justify="end">          
            <v-col cols="12" sm="4" md="4">
                <v-text-field
                    v-model="search"
                    append-icon="mdi-magnify"
                    :label="$t('search')"
                    single-line
                    hide-details
                ></v-text-field>
            </v-col>
        </v-row>

        <!-- Progress Report Button -->
        <v-row justify="end" class="mb-4">
        <v-col cols="auto">
            <v-btn
            color="primary"
            @click="openReportsView"
            >
            <v-icon left>mdi-file-document-outline</v-icon>
            {{$t('viewReports')}}
            </v-btn>
        </v-col>
        </v-row>

        <!-- Progress Report Dialog -->
        <v-dialog
        v-model="showProgressDialog"
        max-width="900"
        >
        <v-card>
            <v-card-title class="headline">
                {{$t('courseProgressReport')}}
                <v-spacer></v-spacer>
                <v-btn icon @click="showProgressDialog = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text v-if="progressReport && progressReport.summary">
                <v-overlay v-if="isLoading">
                    <v-progress-circular indeterminate></v-progress-circular>
                </v-overlay>
          
                <!-- Summary Cards -->
                <v-row class="mb-6">
                    <v-col cols="12" md="3">
                        <v-card outlined>
                            <v-card-text>
                                <div class="d-flex align-center mb-2">
                                    <v-icon color="blue" class="mr-2">mdi-school</v-icon>
                                    <span class="text-subtitle-2 grey--text">{{$t('totalCourses')}}</span>
                                </div>
                                <div class="text-h4 font-weight-bold">
                                    {{progressReport.summary.totalEnrolledCourses}}
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-card outlined>
                            <v-card-text>
                                <div class="d-flex align-center mb-2">
                                    <v-icon color="green" class="mr-2">mdi-check-circle</v-icon>
                                    <span class="text-subtitle-2 grey--text">{{$t('completedCourses')}}</span>
                                </div>
                                <div class="text-h4 font-weight-bold">
                                    {{progressReport.summary.completedCourses}}
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-card outlined>
                            <v-card-text>
                                <div class="d-flex align-center mb-2">
                                    <v-icon color="orange" class="mr-2">mdi-progress-clock</v-icon>
                                    <span class="text-subtitle-2 grey--text">{{$t('inProgress')}}</span>
                                </div>
                                <div class="text-h4 font-weight-bold">
                                    {{progressReport.summary.inProgressCourses}}
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-card outlined>
                            <v-card-text>
                                <div class="d-flex align-center mb-2">
                                    <v-icon color="purple" class="mr-2">mdi-chart-line</v-icon>
                                    <span class="text-subtitle-2 grey--text">{{$t('averageScore')}}</span>
                                </div>
                                <div class="text-h4 font-weight-bold">
                                    {{progressReport.summary.averageScore.toFixed(1)}}%
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- Detailed Progress -->
                <v-card outlined>
                    <v-card-title>{{$t('detailedProgress')}}</v-card-title>
                    <v-card-text>
                        <v-list>
                            <v-list-item
                                v-for="course in progressReport.courses"
                                :key="course.courseId"
                            >
                                <v-list-item-content>
                                    <v-row align="center">
                                        <v-col cols="12" md="3">
                                            <div class="font-weight-medium">{{course.courseTitle}}</div>
                                            <div class="text-caption grey--text">{{course.courseCategory}}</div>
                                        </v-col>
                                        <v-col cols="12" md="2">
                                            <v-progress-circular
                                                :value="course.completionPercentage"
                                                :color="course.completionPercentage >= 100 ? 'success' : 'primary'"
                                                size="64"
                                            >
                                                {{ course.completionPercentage.toFixed(0) }}%
                                            </v-progress-circular>
                                        </v-col>
                                        <v-col cols="12" md="7">
                                            <v-expansion-panels flat>
                                                <v-expansion-panel>
                                                    <v-expansion-panel-header>
                                                        {{course.quizzes.length}} {{$t('quizzes')}}
                                                    </v-expansion-panel-header>
                                                    <v-expansion-panel-content>
                                                        <v-list dense>
                                                            <v-list-item v-for="quiz in course.quizzes" :key="quiz.quizTopicId">
                                                                <v-list-item-content>
                                                                    <v-row align="center">
                                                                        <v-col cols="4">{{quiz.quizTitle}}</v-col>
                                                                        <v-col cols="2">
                                                                            <v-chip
                                                                                small
                                                                                :color="quiz.status === 'Passed' ? 'success' : 'warning'"
                                                                                text-color="white"
                                                                            >
                                                                                {{quiz.status}}
                                                                            </v-chip>
                                                                        </v-col>
                                                                        <v-col cols="2">{{quiz.bestScore}}%</v-col>
                                                                        <v-col cols="2">{{quiz.attempts}} {{$t('attempts')}}</v-col>
                                                                        <v-col cols="2">{{formatTime(quiz.timeTaken)}}</v-col>
                                                                    </v-row>
                                                                </v-list-item-content>
                                                            </v-list-item>
                                                        </v-list>
                                                    </v-expansion-panel-content>
                                                </v-expansion-panel>
                                            </v-expansion-panels>
                                        </v-col>
                                    </v-row>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </v-card-text>
                </v-card>
            </v-card-text>

            <v-card-text v-else>
                <v-alert type="error" v-if="error">
                    Failed to load progress report
                </v-alert>
                <v-skeleton-loader v-else type="card"></v-skeleton-loader>
            </v-card-text>
        </v-card>
        </v-dialog>
        
        <v-data-table
            :headers="headersResults"
            :items="itemsResults"
            :search="search"
            class="elevation-1"
        >
            <template v-slot:top>
                <v-dialog
                    v-model="dialog"
                    max-width="1000"
                >
                    <v-card>
                        <Loading/>
                        <v-card-text>
                            <v-container class="px-0"> 
                                <v-row justify="center">
                                    <v-col cols="6" sm="2" class="font-weight-black"><h3>{{$t('resultSheet')}}</h3></v-col>
                                </v-row>                                  
                                <v-row>
                                    <v-col cols="12" sm="1">
                                        <span class="font-weight-medium">{{$t('name')}}</span>
                                    </v-col>
                                    <v-col cols="12" sm="3">
                                        <span>{{participantName}}</span>                                          
                                    </v-col>
                                    <v-col cols="12" sm="1">
                                        <span class="font-weight-medium">{{$t('email')}}</span>
                                    </v-col>
                                    <v-col cols="12" sm="3">
                                        <span>{{participantEmail}}</span>
                                    </v-col>
                                    <v-col cols="12" sm="1">
                                        <span class="font-weight-medium">{{$t('title')}}</span>
                                    </v-col>
                                    <v-col cols="12" sm="3">
                                        <span>{{quizName}}</span>
                                    </v-col>
                                </v-row>                                                                  
                                <v-row>
                                    <v-col cols="12" sm="1">
                                        <span class="font-weight-medium">{{$t('timeTakenMin')}}</span>
                                    </v-col>
                                    <v-col cols="12" sm="3">
                                        <span>{{quizAssignTime}} min</span>
                                    </v-col>
                                    <v-col cols="12" sm="1">
                                        <span class="font-weight-medium">{{$t('marks')}}</span>
                                    </v-col>
                                    <v-col cols="12" sm="3">
                                        <span>{{quizAssignMarks}}</span>
                                    </v-col>
                                    <v-col cols="12" sm="2">
                                        <span class="font-weight-medium">{{$t('marksObtained')}}</span>
                                    </v-col>
                                    <v-col cols="12" sm="2">
                                        <span>{{userObtainedMarks}}</span>
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col cols="12" sm="1">
                                        <span class="font-weight-medium">{{$t('totalPointerIncident')}}</span>
                                    </v-col>
                                    <v-col cols="12" sm="3">
                                        <span>{{pointerLeaveCountSumExamine}}</span>
                                    </v-col>
                                    <v-col cols="12" sm="1">
                                        <span class="font-weight-medium">{{$t('totalExamWindowsLeft')}}</span>
                                    </v-col>
                                    <v-col cols="12" sm="3">
                                        <span>{{visibilitychangeCountSumExamine}}</span>
                                    </v-col>
                                    <v-col cols="12" sm="1">
                                        <span class="font-weight-medium">{{$t('totalPenalty')}}</span>
                                    </v-col>
                                    <v-col cols="12" sm="3">
                                        <span>{{totalPanalty}}</span>
                                    </v-col>
                                </v-row>
                                <v-data-table
                                    v-if="checkResultCount==true"
                                    :headers="headersSingleResult"
                                    :items="itemsSingleResult"
                                    disable-pagination
                                    hide-default-footer
                                >
                                    <template v-slot:[`item.isAnswerSkipped`]="{item}">
                                        <span>{{formatAnswerSkipped(item)}}</span> 
                                    </template>
                                    <template v-slot:[`item.questionMark`]="{item}">
                                        <span>{{formatQuestionMarks(item)}}</span> 
                                    </template>
                                    <template v-slot:[`item.userObtainedQuestionMark`]="{item}">
                                        <span>{{formatUserMarks(item)}}</span> 
                                    </template>
                                    <template v-slot:[`item.correctAnswer`]="{item}">
                                        <span>{{formatCorrectAnswer(item)}}</span> 
                                    </template>
                                </v-data-table>
                            </v-container>                           
                        </v-card-text>
                    </v-card>
                </v-dialog>
            </template>
            <template v-slot:[`item.actions`]="{item}">
                <v-icon
                    class="mr-2"
                    small
                    @click="getDetailResult(item)"
                >
                    description
                </v-icon>
                <v-icon
                    small
                    v-if="chkCertificateVisibility(item)"
                    @click="getCertificate(item)"
                >
                    card_giftcard
                </v-icon>
                <v-icon
                    small
                    v-if="isWhatsappShareVisible(item)"
                    @click="shareWhatsapp(item)"
                    color="green"
                >
                    mdi-whatsapp
                </v-icon>
            </template>
            <template v-slot:[`item.quizMark`]="{item}">
                <span>{{formatQuizMarks(item)}}</span> 
            </template>
            <template v-slot:[`item.userObtainedQuizMark`]="{item}">
                <span>{{formatObtainedMarks(item)}}</span> 
            </template>
            <template v-slot:[`item.isExamined`]="{item}">
                <span>{{formatStatus(item)}}</span> 
            </template>
            <template v-slot:[`item.quizTime`]="{item}">
                <span>{{formatQuizTime(item)}}</span> 
            </template>
            <template v-slot:[`item.dateAdded`]="{item}">
                <span>{{formatDateTime(item)}}</span> 
            </template>
        </v-data-table>


    </v-container>
</template>

<script>
import Loading from '../../components/common/Loading'
import config from '../../../public/config'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'

export default {
    name:'Students',
    components:{
      Loading
    },
    data(){
        return{
            search: '',
            dialog:false,
            userInfo:{},
            quizes:[],
            itemsResults:[],
            filteredItemsResults:[],
            headersResults:[
                {text:this.$t('name'),value:'fullName'},
                {text:this.$t('email'),value:'email'},
                {text:this.$t('title'),value:'quizTitle'},
                {text:this.$t('timeMin'),value:'quizTime'},
                {text:this.$t('timeTakenMin'),value:'timeTaken'},
                {text:this.$t('marks'),value:'quizMark'},
                {text:this.$t('marksObtained'),value:'userObtainedQuizMark'},
                {text:this.$t('status'),value:'isExamined'},               
                {text:this.$t('attemptNumber'),value:'attemptCount'},
                {text:this.$t('attemptDateTime'),value:'dateAdded'},
                {text:this.$t('actions'), value:'actions', sortable: false },
            ],
            itemsSingleResult:[],
            headersFiltered:[
                {text:this.$t('question'),value:'questionDetail'},
                {text:this.$t('answerInput'),value:'userAnswer'},
                {text:'Correct Answer',value:'correctAnswer'},
                {text:this.$t('skipped'),value:'isAnswerSkipped'},
                {text:this.$t('questionMark'),value:'questionMark'},
                {text:this.$t('yourMarks'),value:'userObtainedQuestionMark'},
                {text:this.$t('pointerLeaveCount'),value:'pointerLeaveCount'},
                {text:this.$t('visibilitychangeCount'),value:'visibilitychangeCount'},
                {text:this.$t('answerExplanation'),value:'answerExplanation'},
            ],
            allowCorrectOption:null,
            name:'',
            email:'',
            quizTitle:'',
            timeTaken:'',
            quizMarks:'',
            marksObtained:'',
            visibilitychangeCountSum:0,
            pointerLeaveCountSum:0,
            isAvailableSingleResult:false,
            showReportsDialog: false,
            activeReportTab: 0,
            topParticipants: [],
            completionReport: [],
            courseDetailsHeaders: [
                { text: this.$t('courseName'), value: 'courseName' },
                { text: this.$t('passedQuizzes'), value: 'passedQuizCount' },
                { text: this.$t('totalQuizzes'), value: 'totalQuizCount' },
                { text: this.$t('averageScore'), value: 'averageScore' }
            ],
            showProgressDialog: false,
            progressReport: {
                summary: {
                    totalEnrolledCourses: 0,
                    completedCourses: 0,
                    inProgressCourses: 0,
                    overallProgress: 0,
                    averageScore: 0
                },
                courses: []
            },
            isLoading: false,
            error: null,
        }
    },
    methods:{ 
        async openReportsView() {
            // Reset the data
            this.progressReport = {
                summary: {
                    totalEnrolledCourses: 0,
                    completedCourses: 0,
                    inProgressCourses: 0,
                    overallProgress: 0,
                    averageScore: 0
                },
                courses: []
            }
            
            // Open the dialog
            this.showProgressDialog = true
            
            // Fetch the data
            await this.fetchProgressReport(this.userInfo.userId)
        },
        // async openReportsView() {
        //     this.showProgressDialog = true
            
        //     await this.fetchProgressReport(this.userInfo.userId)
        // },

        formatTime(hours) {
            if (hours == null) return 'N/A';
            const h = Math.floor(hours);
            const m = Math.round((hours - h) * 60);
            return h > 0 ? `${h}h ${m}m` : `${m}m`;
        },

        getCompletionPercentage(course) {
            return course && typeof course.completionPercentage === 'number' 
                ? course.completionPercentage.toFixed(0) 
                : '0';
        },

        async fetchProgressReport(id) {
            try {
                this.$store.dispatch('dashboard/applyLoading')
                
                const response = await this.$store.dispatch('report/fetchStudentProgressReport', id)
                
                // Use Vue.set to ensure reactivity for nested objects
                this.$set(this, 'progressReport', {
                    summary: response.data.summary,
                    courses: [...response.data.courses]
                })
                
                this.$store.dispatch('dashboard/cancelLoading')
            } catch (error) {
                console.error('Error fetching progress report:', error)
                this.$router.push({ name: 'NoInternet' })
            }
        },
        // async fetchProgressReport(id) {
        //     console.log(id)
        //     try {
        //     // Replace this with your actual API call
        //     this.$store.dispatch('dashboard/applyLoading')
        //     const response = await this.$store.dispatch('report/fetchStudentProgressReport', id)
        //     console.log(response.data)
        //     this.progressReport = response.data
        //     console.log('Progress Report',this.progressReport)
        //     this.$store.dispatch('dashboard/cancelLoading')
        //     } catch (error) {

        //     console.error(error)
        //     this.$router.push({ name: 'NoInternet' })
        //     }
        // },

        generateTopParticipantsPdf() {
            const doc = new jsPDF()
            doc.autoTable({
                head: [['Rank', 'Email', 'Completed Courses', 'Last Completion']],
                body: this.topParticipants.map((item, index) => [
                    `#${index + 1}`,
                    item.email,
                    item.completedCount,
                    this.formatDateTime(item.lastCompletionDate)
                ])
            })
            doc.save('top-participants.pdf')
        },

        generateCompletionReportPdf() {
            const doc = new jsPDF()
            doc.autoTable({
                head: [['Name', 'Email', 'Total Courses', 'Passed Courses', 'Average Score']],
                body: this.completionReport.map(item => [
                    item.fullName,
                    item.email,
                    item.summary.totalAttemptedCourses,
                    item.summary.passedCourses,
                    `${item.summary.averageScore.toFixed(1)}%`
                ])
            })
            doc.save('completion-report.pdf')
        },
        isWhatsappShareVisible(item) {
            return this.formatStatus(item) === 'Passed' && 
                   item.certificateTemplateId != null
        },  
         shareWhatsapp(item) {
            const certificateUrl = `${config.hostname}/api/Quiz/ShowCertificate/${item.certificateTemplateId}/${item.userId}/${item.quizResponseInitialId}`
            
            const shareText = encodeURIComponent(`Check out my quiz certificate! ${certificateUrl}`)
            
            window.open(`https://api.whatsapp.com/send?text=${shareText}`, '_blank')
        },  
        getQuizesFiltered(id){
            this.$store.dispatch('dashboard/applyLoading')
            this.$store.dispatch('report/fetchQuizesFiltered',id)
            .then((response)=>{
                this.$store.dispatch('dashboard/cancelLoading')
                this.quizes=response.data
            })
            .catch((err)=>{
                console.log(err)
                this.$router.push({name:'NoInternet'})
            })
        },
        quizResultsFiltered(id){
            this.$store.dispatch('dashboard/applyLoading')
            this.$store.dispatch('report/fetchResultsFiltered',id)
            .then((response)=>{
                this.$store.dispatch('dashboard/cancelLoading')
                //console.log(response.data)
                this.itemsResults=response.data
                this.filteredItemsResults=this.itemsResults
            })
            .catch((err)=>{
                console.log(err)
                this.$router.push({name:'NoInternet'})
            })
        },
        filterResult(obj){          
            if(obj==null){
                this.itemsResults=this.filteredItemsResults
            }else{
                this.itemsResults=this.filteredItemsResults.filter(function(item){
                    return item.quizTopicId==obj.quizTopicId
                })
            }
        },
        quizResult(id){
            this.$store.dispatch('dashboard/applyLoading')
            this.$store.dispatch('dashboard/fetchFinishedExamResult',id)
            .then((response)=>{
                this.$store.dispatch('dashboard/cancelLoading')
                this.itemsSingleResult=response.data
                if(this.itemsSingleResult[0].visibilitychangeCount=='n/a'){
                    this.isAvailableSingleResult=false
                }else{
                    this.isAvailableSingleResult=true
                }

                this.allowCorrectOption=this.itemsSingleResult[0].allowCorrectOption
                this.name=this.itemsSingleResult[0].fullName
                this.email=this.itemsSingleResult[0].email
                this.quizTitle=this.itemsSingleResult[0].quizTitle
                this.timeTaken=this.itemsSingleResult[0].timeTaken
                this.pointerLeaveCountSum=this.itemsSingleResult[0].pointerLeaveCountSum
                this.visibilitychangeCountSum=this.itemsSingleResult[0].visibilitychangeCountSum
                this.quizMarks=this.itemsSingleResult[0].quizMark==0?'n/a':this.itemsSingleResult[0].quizMark
                this.marksObtained=this.itemsSingleResult[0].quizMark==0?'n/a':(this.itemsSingleResult[0].userObtainedQuizMark>this.itemsSingleResult[0].quizMark?this.itemsSingleResult[0].quizMark:this.itemsSingleResult[0].userObtainedQuizMark)
            })
            .catch((err)=>{
                console.log(err)
                this.$router.push({name:'NoInternet'})
            })
        },
        getDetailResult(item){
            this.dialog=true
            this.quizResult(item.quizResponseInitialId)           
        },
        chkCertificateVisibility(item){
            if(this.userInfo.roleName=='Admin'){
                return false
            }
            else if(item.certificateTemplateId!=null && item.quizPassMarks==0){
                return true
            }else if(item.certificateTemplateId!=null && item.quizPassMarks!=0 && item.userObtainedQuizMark>=item.quizPassMarks){
                return true
            }else{
                return false
            }
        },
        getCertificate(item){        
            /* this.$store.dispatch('report/storeCertificateInfo',item)
            this.$router.push({name:'Template'}) */
            window.open(config.hostname+'/api/Quiz/ShowCertificate/'+item.certificateTemplateId+'/'+item.userId+'/'+item.quizResponseInitialId)
        },
        formatQuizTime(item){
            return item.quizTime==0?'n/a':item.quizTime
        },
        formatQuizMarks(item){
            return item.quizMark==0?'n/a':item.quizMark
        },
        formatObtainedMarks(item){
            return item.isExamined==false?'':(item.quizMark==0?'n/a':item.userObtainedQuizMark)
        },
        formatStatus(item){           
            return item.isExamined==true && item.quizMark>0 && (item.userObtainedQuizMark>=item.quizPassMarks)?'Passed':(item.quizMark==0?'n/a':(item.isExamined==false?'Pending':'Failed'))
        },
        formatAnswerSkipped(item){
            return item.isAnswerSkipped==true?'yes':'no'
        },
        formatQuestionMarks(item){
            return item.questionMark==0?'n/a':item.questionMark
        },
        formatUserMarks(item){
            return item.questionMark==0?'n/a':item.userObtainedQuestionMark
        },
        formatDateTime(item){
            return item.dateAdded.replace('T',' ').substring(0,19)
        },
        formatCorrectAnswer(item){
            return item.correctAnswer!=''?item.correctAnswer.replace(/#####/g,','):''
        },
    },
    computed:{
        participantName:function(){
            return this.name
        },
        participantEmail:function(){
            return this.email
        },
        quizName:function(){
            return this.quizTitle
        },
        quizAssignTime:function(){
            return this.timeTaken
        },
        quizAssignMarks:function(){
            return this.quizMarks
        },
        userObtainedMarks:function(){
            return this.marksObtained
        },
        headersSingleResult:function(){
            return this.userInfo.roleName=='Admin'?this.headersFiltered:
            (this.userInfo.roleName=='Student' && this.allowCorrectOption==true?this.headersFiltered:this.headersFiltered.filter(function(item){
                return item.text!='Correct Answer'
            }))
        },
        pointerLeaveCountSumExamine:function(){
            return this.pointerLeaveCountSum
        },
        visibilitychangeCountSumExamine:function(){
            return this.visibilitychangeCountSum
        },
        totalPanalty:function(){
            return this.visibilitychangeCountSum+this.pointerLeaveCountSum
        },
        checkResultCount(){
            return this.isAvailableSingleResult
        },
        isProgressDataLoading() {
            return !this.progressReport.summary
        },
    },
    created(){
        this.userInfo=this.$store.getters['dashboard/userInfo']
        if(this.userInfo.roleName=='Student'){
            this.quizResultsFiltered(this.userInfo.userId)
            this.getQuizesFiltered(this.userInfo.userId)
        }
    }
}
</script>